import React, { useState } from "react";
import { toast } from "react-toastify";
import { useCreateQuoteMutation, useSubmitQuoteMutation } from "../../redux/api/quoteApi";

/**
 * FAQ 页面专用的 PCB Layout 报价申请表单。
 * 参考文档中的 Online PCB Layout Quote 图片实现。
 */
const FaqQuoteForm = () => {
  const [createQuote, { isLoading: isCreating }] = useCreateQuoteMutation();
  const [submitQuote, { isLoading: isSubmitting }] = useSubmitQuoteMutation();

  const [formValues, setFormValues] = useState({
    service: "PCB Layout Design",
    pcbType: "Regular Rigid PCB",
    layers: "2",
    length: "",
    width: "",
    padCount: "",
    edaSoftware: "Other",
    fileStorage: "Offline",
    orderPcbaAtCreatyea: "Yes",
    componentSides: "Both sides",
    provideFootprintFile: "Yes",
    componentsMatchingService: "No",
    deliveryFormat: ["Source document", "Gerber"],
    remark: "",
    name: "",
    email: "",
  });

  const [files, setFiles] = useState({
    layoutRequirements: null,
    schematicPcbFile: null,
    otherFiles: null,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      const updatedDeliveryFormat = checked
        ? [...formValues.deliveryFormat, value]
        : formValues.deliveryFormat.filter((item) => item !== value);
      setFormValues({ ...formValues, deliveryFormat: updatedDeliveryFormat });
    } else {
      setFormValues({ ...formValues, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    setFiles({ ...files, [name]: selectedFiles[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.email || !formValues.name) {
      toast.error("Please fill in your name and email.");
      return;
    }

    try {
      const payload = {
        quoteType: "pcb_layout_quote", // 自定义类型标识
        customerInfo: {
          name: formValues.name,
          email: formValues.email,
        },
        spec: {
          ...formValues,
        },
        sessionId: `faq-quote-${Date.now()}`,
      };

      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      
      if (files.layoutRequirements) formData.append("attachments", files.layoutRequirements);
      if (files.schematicPcbFile) formData.append("attachments", files.schematicPcbFile);
      if (files.otherFiles) formData.append("attachments", files.otherFiles);

      // 1. 创建报价单
      const createRes = await createQuote(formData).unwrap();
      const quoteId = createRes?.quote?._id;

      if (quoteId) {
        // 2. 提交到人工审核流程
        await submitQuote({ 
          id: quoteId, 
          message: `PCB Layout Quote Request. Remark: ${formValues.remark}`,
          sessionId: payload.sessionId 
        }).unwrap();
        
        toast.success("Your quote request has been submitted successfully!");
        // 重置部分表单
        setFormValues({
          ...formValues,
          remark: "",
          length: "",
          width: "",
          padCount: "",
        });
        setFiles({
          layoutRequirements: null,
          schematicPcbFile: null,
          otherFiles: null,
        });
      }
    } catch (error) {
      toast.error(error?.data?.message || "Failed to submit request. Please try again.");
    }
  };

  const renderRadioGroup = (label, name, options) => (
    <div className="mb-4 grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-4">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setFormValues({ ...formValues, [name]: opt })}
            className={`px-4 py-1.5 text-sm rounded-md border transition-all ${
              formValues[name] === opt
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <section className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">
        Online PCB Layout Quote
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 基本信息 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50/30 p-4 rounded-lg mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Email *</label>
            <input
              type="email"
              name="email"
              value={formValues.email}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* 核心字段 */}
        {renderRadioGroup("Service", "service", ["PCB Layout Design", "PCB Layout Modify"])}

        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] items-start gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700 mt-2">Layout Requirements</label>
          <div className="flex items-center gap-4">
            <input
              type="file"
              name="layoutRequirements"
              onChange={handleFileChange}
              className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <span className="text-blue-600 text-sm cursor-pointer hover:underline">Template</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700">Schematic & pcb file</label>
          <input
            type="file"
            name="schematicPcbFile"
            onChange={handleFileChange}
            className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700">Other Files</label>
          <input
            type="file"
            name="otherFiles"
            onChange={handleFileChange}
            className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {renderRadioGroup("PCB Type", "pcbType", ["Regular Rigid PCB", "Flex PCB", "Power PCB", "HDI PCB", "Other"])}

        <div className="mb-4 grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Layer</label>
          <div className="flex flex-wrap gap-2">
            {["1", "2", "4", "6", "8", "10", "12", "14", "16", "18", "20", "22"].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setFormValues({ ...formValues, layers: num })}
                className={`w-10 h-10 flex items-center justify-center text-sm rounded-md border transition-all ${
                  formValues.layers === num
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                }`}
              >
                {num}
              </button>
            ))}
            <select
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              onChange={(e) => setFormValues({ ...formValues, layers: e.target.value })}
              value={["1", "2", "4", "6", "8", "10", "12", "14", "16", "18", "20", "22"].includes(formValues.layers) ? "" : formValues.layers}
            >
              <option value="" disabled>More</option>
              {[24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48, 50].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700">Dimensions</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              name="length"
              value={formValues.length}
              onChange={handleInputChange}
              placeholder="Length"
              className="w-24 px-3 py-2 border border-gray-300 rounded-md"
            />
            <span>*</span>
            <input
              type="number"
              name="width"
              value={formValues.width}
              onChange={handleInputChange}
              placeholder="Width"
              className="w-24 px-3 py-2 border border-gray-300 rounded-md"
            />
            <span className="text-gray-500 ml-2">mm</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700">Pad Count</label>
          <input
            type="number"
            name="padCount"
            value={formValues.padCount}
            onChange={handleInputChange}
            className="w-32 px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        {renderRadioGroup("EDA Software", "edaSoftware", ["Other"])}
        {renderRadioGroup("File Storage", "fileStorage", ["Offline"])}
        {renderRadioGroup("Order PCBA at Creatyea", "orderPcbaAtCreatyea", ["Yes", "No"])}
        {renderRadioGroup("Component Sides", "componentSides", ["Top only", "Bottom only", "Both sides"])}
        {renderRadioGroup("Provide Footprint File", "provideFootprintFile", ["Yes", "No"])}
        {renderRadioGroup("Components matching service", "componentsMatchingService", ["Yes", "No"])}

        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] items-start gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700 mt-1">Delivery Format</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              "Source document", "Gerber", "BOM", "Pick & place", 
              "Mechanical drawing (DXF)", "Fabrication layer (PDF)", 
              "3D model (STEP)"
            ].map((format) => (
              <label key={format} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  value={format}
                  checked={formValues.deliveryFormat.includes(format)}
                  onChange={handleInputChange}
                  className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                />
                {format}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr] items-start gap-4 mb-6">
          <label className="text-sm font-medium text-gray-700 mt-2">Remark</label>
          <textarea
            name="remark"
            value={formValues.remark}
            onChange={handleInputChange}
            rows="4"
            placeholder="Leave a remark if you have any request"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={isCreating || isSubmitting}
            className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 shadow-md"
          >
            {isCreating || isSubmitting ? "Submitting..." : "Submit Quote Request"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default FaqQuoteForm;
